import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  styled,
  useMediaQuery,
  Link as MUILink,
} from "@mui/material";
import { Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

import OTPInput from "react-otp-input";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import {
  useRequestOTPMutation,
  useSetNewPasswordPutMutation,
  useValidateOTPPutMutation,
} from "apis/OTP";

import useCountdown from "hooks/useCountDown";
import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";

import { homeRoute } from "Routes/constants";
import { otpVerificationType } from "common/constant";

const step1 = "Verify Email";
const step2 = "Verify OTP";
const step3 = "Change Password";

export const ResendOTPButton = styled(MUILink)(
  ({ theme }) => `
    cursor :  pointer;
    font-weight:bold;
    font-size:14px;
    :disabled {
    color: ${theme.palette.action.disabled};
    cursor:default
    }
`
);

const requestOTPType = otpVerificationType["ForgotPassword"];

const ForgotPasswordModal = ({ open, toggle, isFireSafety,isUtilityTunnel }) => {
  const [setPassword, { isLoading }] = useRequestOTPMutation();
  const [verifyOTP, { isLoading: isVerifyOtpLoading }] =
    useValidateOTPPutMutation();
  const [setNewPassword, { isLoading: isChangePassswordLoading }] =
    useSetNewPasswordPutMutation();
  const [forgotPasswordStep, setForgotPasswordStep] = useState(step1);
  const [userOtp, setUserOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEnableResendOTP, setIsEnableResendOTP] = useState(false);

  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const handleCountdownFinished = () => {
    setIsEnableResendOTP(true);
  };

  const { minutes, seconds, startTimer, resetTimer } = useCountdown(
    0,
    2,
    0,
    handleCountdownFinished
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      isMobileView: "",
      otp: "",
      confirmPassword: "",
      forgotPasswordStep: "",
      secureToken: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .matches(
          /(?:\b[6789]\d{9}\b|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,3}\b)/,
          "Please enter valid email or mobile number"
        )
        .required("Please enter your email or mobile number"),
      otp: Yup.string().when(["forgotPasswordStep", "isMobileView"], {
        is: (forgotPasswordStep, isMobileView) =>
          forgotPasswordStep === step2 && isMobileView,
        then: Yup.string()
          .required("Please enter otp")
          .matches(/^[0-9]+$/, "Must be only digits")
          .min(6, "Must be exactly 6 digits")
          .max(6, "Must be exactly 6 digits"),
      }),
      password: Yup.string().when("forgotPasswordStep", {
        is: step3,
        then: Yup.string().required("Please enter password"),
      }),
      confirmPassword: Yup.string().when("forgotPasswordStep", {
        is: step3,
        then: Yup.string()
          .when("password", {
            is: (val) => !!(val && val.length > 0),
            then: Yup.string().oneOf(
              [Yup.ref("password")],
              "Both password need to be the same"
            ),
          })
          .required("Confirm Password Required"),
      }),
    }),
    validateOnChange: false,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const {
    values: { email },
    setFieldValue,
    handleSubmit: handleForgotPassword,
  } = formik;

  const handleSubmit = async ({ email, otp, secureToken, password }) => {
    const isMobileNumber = email.match(/^[0-9]{10}$/);
    if (forgotPasswordStep === step1) {
      try {
        const payload = {
          type: requestOTPType,
          [isMobileNumber ? "mobileNumber" : "email"]: email,
        };
        await setPassword(
          payload,
          "OTP generated, please check your phone or email.",
          isFireSafety,
          isUtilityTunnel
          
        );
        setForgotPasswordStep(step2);
        setFieldValue("forgotPasswordStep", step2);
        startTimer();
      } catch (err) {}
    } else if (forgotPasswordStep === step2) {
      try {
        const {
          data: { secureToken },
        } = await verifyOTP(
          {
            [isMobileNumber ? "mobileNumberOtp" : "emailOtp"]: +otp,
            [isMobileNumber ? "mobileNumber" : "email"]: email,
            type: requestOTPType,
          },
          {},
          isFireSafety,
          isUtilityTunnel
        );
        setForgotPasswordStep(step3);
        setFieldValue("secureToken", secureToken);
        setFieldValue("forgotPasswordStep", step3);
      } catch (error) {}
    } else {
      try {
        await setNewPassword(
          {
            secureToken,
            username: email,
            password,
          },
          {},
          isFireSafety,
          isUtilityTunnel
        );
        toggle();
      } catch (error) {}
    }
  };

  const handleResendOTP = async () => {
    setIsEnableResendOTP(false);
    resetTimer();
    startTimer();
    try {
      const payload = {
        type: requestOTPType,
        [email.match(/^[0-9]{10}$/) ? "mobileNumber" : "email"]:
          encodeURIComponent(email),
      };
      await setPassword(
        payload,
        "OTP generated, please check your phone or email."
      );
    } catch (err) {}
  };

  const handleChangeOtp = (OTP) => {
    setUserOtp(OTP);
    setFieldValue("otp", OTP);
  };

  useEffect(() => {
    setFieldValue("isMobileView", isMobileDevice);
  }, [isMobileDevice]);

  return (
    <>
      <Modal
        isOpen={open}
        handleClose={toggle}
        className="fogot-password-modal-wrapper"
        title="Forgot Password?"
      >
        <div className="p-2 mt-1 form-wrapper">
          {forgotPasswordStep === step1 && (
            <>
              <div className="emailtext-wrapper">
                <p className="medium-font">
                  Enter your email and instructions will be sent to you!
                </p>
              </div>
              <Form onSubmit={handleForgotPassword}>
                <InputFieldText
                  label="Enter email or mobile number"
                  name="email"
                  formik={formik}
                  placeholder="Enter email or mobile number"
                />
                <Button
                  variant="contained"
                  className="modal-btn semibold-font"
                  type="submit"
                  disabled={isLoading}
                >
                  <div className="d-flex align-items-center gap-1">
                    Submit
                    {isLoading && <CircularProgress size={20} />}
                  </div>
                </Button>
              </Form>
            </>
          )}
          {forgotPasswordStep === step2 && (
            <Form onSubmit={handleForgotPassword}>
              <p>Enter verification code:</p>
              {isMobileDevice ? (
                <InputFieldText
                  label="Enter OTP"
                  name="otp"
                  formik={formik}
                  placeholder="Enter OTP"
                />
              ) : (
                <OTPInput
                  value={userOtp}
                  inputStyle={
                    "form-control form-control-lg bg-light border-light text-center w-25 mb-3"
                  }
                  containerStyle={"justify-content-between"}
                  onChange={handleChangeOtp}
                  numInputs={6}
                  inputType="number"
                  renderSeparator={<span>{"\u2003"}</span>}
                  shouldAutoFocus
                  renderInput={(props) => <input {...props} />}
                />
              )}
              <Button
                variant="contained"
                className="modal-btn semibold-font"
                type="submit"
                disabled={
                  isVerifyOtpLoading ||
                  (!isMobileDevice && userOtp?.length !== 6)
                }
              >
                <div className="d-flex align-items-center gap-1">
                  Submit
                  {isVerifyOtpLoading && <CircularProgress size={20} />}
                </div>
              </Button>
              <div className="d-flex my-1 justify-content-center mt-2 gap-2">
                <ResendOTPButton
                  component="button"
                  underline="none"
                  onClick={handleResendOTP}
                  disabled={!isEnableResendOTP}
                >
                  Resend OTP
                </ResendOTPButton>
                {!isEnableResendOTP && (
                  <p className="m-0">
                    in {minutes}:{seconds}
                  </p>
                )}
              </div>
            </Form>
          )}
          {forgotPasswordStep === step3 && (
            <Form onSubmit={handleForgotPassword}>
              <InputFieldText
                label="Enter your password"
                name="password"
                formik={formik}
                placeholder="Enter password"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        edge="end"
                        className="d-none d-sm-block"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
              />
              <InputFieldText
                label="Enter your confirm password"
                name="confirmPassword"
                formik={formik}
                placeholder="Enter confirm password"
                type="password"
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
              />
              <Button
                variant="contained"
                className="modal-btn semibold-font"
                type="submit"
                disabled={isChangePassswordLoading}
              >
                <div className="d-flex align-items-center gap-1">
                  Submit
                  {isChangePassswordLoading && <CircularProgress size={20} />}
                </div>
              </Button>
            </Form>
          )}
          <Link
            to={homeRoute}
            className="medium-font ps-2 sign-in-text text-center m-2 d-block"
            onClick={toggle}
          >
            Back to login
          </Link>
        </div>
      </Modal>
    </>
  );
};

ForgotPasswordModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
  isFireSafety: PropTypes.bool,
  isUtilityTunnel:PropTypes.bool
};

export default ForgotPasswordModal;
